import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IRecentSearchForm, SEARCH, TSearchState } from './searchTypes';
import { IPortSearchForm } from '../port/portTypes';
import { EOutputType, ESelectionType } from '../../shared/sharedEnums';
import { getRecentSearchesFromLocalStorage, storeRecentSearchesInLocalStorage } from '../../utils/helpers';
import { IFilterFormData, IPortfolioFormData, ISelectedVesselsFormData } from '../../shared/sharedTypes';
import { trackEventWithContext, trackExceptionWithContext } from '../../services/application-insights/appInsights';
import {
	AppInsightsActions,
	AppInsightsComponents,
	AppInsightsErrorTypes,
} from '../../services/application-insights/appInsightsConstants';

const initialState: TSearchState = {
	selectionType: ESelectionType.None,
	selectionName: '',
	selectedVesselsList: [],
	outputType: EOutputType.Summary,
	selectionTypeObject: undefined,
	isBusy: false,
};

const searchSlice = createSlice({
	name: SEARCH,
	initialState,
	reducers: {
		setSelectedVesselsForOutputRequest: (
			state: TSearchState,
			action: PayloadAction<{ outputSelection: ISelectedVesselsFormData }>,
		) => {
			try {
				const { outputSelection } = action.payload;
				const selectionType = outputSelection.selectionType;

				// Track vessel selection attempt
				trackEventWithContext(AppInsightsActions.SEARCH_ENTITY_SELECT, {
					component: AppInsightsComponents.SEARCH_SLICE,
					action: AppInsightsActions.SEARCH_ENTITY_SELECT,
					additionalData: {
						selectionType,
						outputType: outputSelection.outputType,
						selectionName: outputSelection.selectionName,
						vesselCount: outputSelection.selectedVesselsList?.length || 0,
					},
				});
				// Handle recent search storage for different selection types
				if (selectionType === ESelectionType.Port) {
					try {
						let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
						if (recentSearches) {
							const existingPort = recentSearches.find(
								(port) =>
									port.type === 'Port' &&
									port.name === (outputSelection.selectionTypeObject as IPortSearchForm).name &&
									port.country === (outputSelection.selectionTypeObject as IPortSearchForm).country,
							);
							if (!existingPort) {
								const portObj = outputSelection.selectionTypeObject as IPortSearchForm;
								const { name, country } = portObj;
								recentSearches = recentSearches
									? [{ type: 'Port', name, country }, ...recentSearches.slice(0, 5)]
									: [{ type: 'Port', name, country }];
								storeRecentSearchesInLocalStorage(recentSearches);
								// Track recent search storage
								trackEventWithContext(AppInsightsActions.SEARCH_RECENT_SELECT, {
									component: AppInsightsComponents.SEARCH_SLICE,
									action: AppInsightsActions.SEARCH_RECENT_SELECT,
									additionalData: { entityType: 'port', portName: name, country },
								});
							}
						}
					} catch (error) {
						trackExceptionWithContext(error as Error, {
							component: AppInsightsComponents.SEARCH_SLICE,
							action: AppInsightsActions.SEARCH_RECENT_SELECT,
							additionalData: {
								errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
								selectionType: 'port',
							},
						});
					}
				}

				if (selectionType === ESelectionType.Vessel) {
					try {
						let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
						if (recentSearches) {
							const existingVessel = recentSearches.find(
								(vessel) => vessel.type === 'Vessel' && vessel.imoNumber === outputSelection.selectedVesselsList[0].imoNumber,
							);
							if (!existingVessel) {
								const { imoNumber, name, sectorName } = outputSelection.selectedVesselsList[0];
								recentSearches = recentSearches
									? [{ type: 'Vessel', imoNumber, name, sector: sectorName }, ...recentSearches.slice(0, 5)]
									: [{ type: 'Vessel', imoNumber, name, sector: sectorName }];
								storeRecentSearchesInLocalStorage(recentSearches);

								// Track recent search storage
								trackEventWithContext(AppInsightsActions.SEARCH_RECENT_SELECT, {
									component: AppInsightsComponents.SEARCH_SLICE,
									action: AppInsightsActions.SEARCH_RECENT_SELECT,
									additionalData: { entityType: 'vessel', imoNumber, vesselName: name },
								});
							}
						}
					} catch (error) {
						trackExceptionWithContext(error as Error, {
							component: AppInsightsComponents.SEARCH_SLICE,
							action: AppInsightsActions.SEARCH_RECENT_SELECT,
							additionalData: {
								errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
								selectionType: 'vessel',
							},
						});
					}
				}

				if (selectionType === ESelectionType.Ownership) {
					try {
						let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
						if (recentSearches) {
							const existingOwnership = recentSearches.find(
								(ownership) => ownership.type === 'Ownership' && ownership.name === outputSelection.selectionName,
							);
							if (!existingOwnership) {
								const name = outputSelection.selectionName;
								recentSearches = recentSearches
									? [{ type: ESelectionType.Ownership, name }, ...recentSearches.slice(0, 5)]
									: [{ type: ESelectionType.Ownership, name }];
								storeRecentSearchesInLocalStorage(recentSearches);

								// Track recent search storage
								trackEventWithContext(AppInsightsActions.SEARCH_RECENT_SELECT, {
									component: AppInsightsComponents.SEARCH_SLICE,
									action: AppInsightsActions.SEARCH_RECENT_SELECT,
									additionalData: { entityType: 'ownership', ownershipName: name },
								});
							}
						}
					} catch (error) {
						trackExceptionWithContext(error as Error, {
							component: AppInsightsComponents.SEARCH_SLICE,
							action: AppInsightsActions.SEARCH_RECENT_SELECT,
							additionalData: {
								errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
								selectionType: 'ownership',
							},
						});
					}
				}

				if (selectionType === ESelectionType.Sector) {
					try {
						let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
						if (recentSearches) {
							const existingSector = recentSearches.find(
								(sector) => sector.type === 'Sector' && sector.name === outputSelection.selectionName,
							);
							if (!existingSector) {
								const name = outputSelection.selectionName;
								recentSearches = recentSearches
									? [{ type: ESelectionType.Sector, name }, ...recentSearches.slice(0, 5)]
									: [{ type: ESelectionType.Sector, name }];
								storeRecentSearchesInLocalStorage(recentSearches);

								// Track recent search storage
								trackEventWithContext(AppInsightsActions.SEARCH_RECENT_SELECT, {
									component: AppInsightsComponents.SEARCH_SLICE,
									action: AppInsightsActions.SEARCH_RECENT_SELECT,
									additionalData: { entityType: 'sector', sectorName: name },
								});
							}
						}
					} catch (error) {
						trackExceptionWithContext(error as Error, {
							component: AppInsightsComponents.SEARCH_SLICE,
							action: AppInsightsActions.SEARCH_RECENT_SELECT,
							additionalData: {
								errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
								selectionType: 'sector',
							},
						});
					}
				}

				if (selectionType === ESelectionType.Filter) {
					try {
						let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
						if (recentSearches) {
							const filter = outputSelection.selectionTypeObject as IFilterFormData;
							const existingFilter = recentSearches?.find((f) => f.type === ESelectionType.Filter && f.id === filter.id);
							if (!existingFilter) {
								const { id, name, scope, isOwnFilter } = filter;
								recentSearches = recentSearches
									? [
											{ id, name, scope, isOwnFilter, type: ESelectionType.Filter } as IRecentSearchForm,
											...recentSearches.slice(0, 5),
										]
									: ([{ id, name, scope, isOwnFilter, type: ESelectionType.Filter } as IRecentSearchForm] as IRecentSearchForm[]);
								storeRecentSearchesInLocalStorage(recentSearches);

								// Track recent search storage
								trackEventWithContext(AppInsightsActions.SEARCH_RECENT_SELECT, {
									component: AppInsightsComponents.SEARCH_SLICE,
									action: AppInsightsActions.SEARCH_RECENT_SELECT,
									additionalData: { entityType: 'filter', filterId: id, filterName: name, scope },
								});
							}
						}
					} catch (error) {
						trackExceptionWithContext(error as Error, {
							component: AppInsightsComponents.SEARCH_SLICE,
							action: AppInsightsActions.SEARCH_RECENT_SELECT,
							additionalData: {
								errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
								selectionType: 'filter',
							},
						});
					}
				}

				if (selectionType === ESelectionType.Portfolio) {
					try {
						let recentSearches: IRecentSearchForm[] = getRecentSearchesFromLocalStorage();
						if (recentSearches) {
							const portfolio = outputSelection.selectionTypeObject as IPortfolioFormData;
							const existingPortfolio = recentSearches.find((p) => p.type === ESelectionType.Portfolio && p.id === portfolio.id);
							if (!existingPortfolio) {
								const { id, name, scope, isOwnPortfolio } = portfolio;
								recentSearches = recentSearches
									? [
											{ id, name, scope, isOwnPortfolio, type: ESelectionType.Portfolio } as IRecentSearchForm,
											...recentSearches.slice(0, 5),
										]
									: ([
											{ id, name, scope, isOwnPortfolio, type: ESelectionType.Portfolio } as IRecentSearchForm,
										] as IRecentSearchForm[]);
								storeRecentSearchesInLocalStorage(recentSearches);

								// Track recent search storage
								trackEventWithContext(AppInsightsActions.SEARCH_RECENT_SELECT, {
									component: AppInsightsComponents.SEARCH_SLICE,
									action: AppInsightsActions.SEARCH_RECENT_SELECT,
									additionalData: { entityType: 'portfolio', portfolioId: id, portfolioName: name, scope },
								});
							}
						}
					} catch (error) {
						trackExceptionWithContext(error as Error, {
							component: AppInsightsComponents.SEARCH_SLICE,
							action: AppInsightsActions.SEARCH_RECENT_SELECT,
							additionalData: {
								errorType: AppInsightsErrorTypes.SEARCH_RECENT_STORAGE_ERROR,
								selectionType: 'portfolio',
							},
						});
					}
				}
				// Update state
				state.selectedVesselsList = outputSelection.selectedVesselsList;
				state.outputType = outputSelection.outputType;
				state.selectionType = outputSelection.selectionType;
				state.selectionName = outputSelection.selectionName;
				state.selectionTypeObject = outputSelection.selectionTypeObject;
				state.isBusy = false;
			} catch (error) {
				trackExceptionWithContext(error as Error, {
					component: AppInsightsComponents.SEARCH_SLICE,
					action: AppInsightsActions.SEARCH_ENTITY_SELECT,
					additionalData: {
						errorType: AppInsightsErrorTypes.SEARCH_SELECTION_ERROR,
						selectionType: action.payload.outputSelection.selectionType,
					},
				});

				// Still update state to avoid breaking the application
				state.selectedVesselsList = action.payload.outputSelection.selectedVesselsList;
				state.outputType = action.payload.outputSelection.outputType;
				state.selectionType = action.payload.outputSelection.selectionType;
				state.selectionName = action.payload.outputSelection.selectionName;
				state.selectionTypeObject = action.payload.outputSelection.selectionTypeObject;
				state.isBusy = false;
			}
		},
	},
});

export const { setSelectedVesselsForOutputRequest } = searchSlice.actions;
export default searchSlice.reducer;
